import '@zebra-fed/icons/Enterprise/index.css';
import Snackbar from '@material-ui/core/Snackbar';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
} from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

// Internal imports
import { AuthContext } from '../../authentication/Auth';
import { SSOSettingsContext } from '../../authentication/SSOSettings';
import { MessageResponseContext } from '../../data/MessageLocalizationProvider';
import firebaseUtil from '../../data/FirebaseUtil';
import { fetchUsers, getTotalCount, fetchLastPage, getAppSettings, getSSOParameterMapping } from './userQueries';

import { ROLES, RESOURCES, usePermissions, withRBAC } from '../../authentication/rbac';

// Components
import AddUserModal from '../../components/modals/AddUserModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { ButtonGroup, CommonButton } from '../../components/common/Button';
import BottomNavLinks from '../../components/BottomNavLinks';
import NumberToDate from '../users/components/NumberToDate';
import Toolbar from '../../components/common/Toolbar';
import Table from '../../components/common/Table';

import {
  ContentWrapper,
  CommonPageHeader,
  CommonPageTitle,
  SnackbarContent,
} from '../../styled-components/shared';

const MainContent = styled.div`
  border-bottom: none;
  border: 1px solid #e0e3e9;
  margin-top: 10px;
  @media screen and (max-width: 750px) {
    margin-top: 60px;
  }
`;

const CuzCell = styled.p`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  line-height: 20px;
  padding: 0px 0px 0px 16px;;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`;

const CustomHeader = styled.p`
  padding: 0px 0px 0px 16px;
  font-weight: bold;
  text-wrap-mode: nowrap;
  align-self: 'center';
  text-align: ${props => (props.left ? 'left' : 'center')};
`;

const DEFAULT_ROWS_PER_PAGE = 10;
const USER_ACTIONS = {
  CREATE_USER: 'CREATE_USER',
  EDIT_USER: 'EDIT_USER'
};

const DEFAULT_ORDER_BY = 'reportedAt'
const DEFAUTL_ORDER_DIRECTION = 'desc'

function ManageUsersPage() {
  const [data, setData] = useState([]);
  const [error, setError] = useState(false);
  const [snackbarVisible, setSnackBarVisible] = useState(false);
  const [snackbarMessage, setSnackBarMessage] = useState('');
  const [currentUser] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [deletedUser, setDeletedUser] = useState(null);
  const [deletedUserEmail, setDeletedUserEmail] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [searchField, setSearchField] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [managerData, setManagerData] = useState({});
  const [isSSOMappingEnabled, setIsSSOMappingEnabled] = useState(false);

  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderDirection, setOrderDirection] = useState(DEFAUTL_ORDER_DIRECTION);
  const [cursors, setCursors] = useState(null);

  const context = useContext(AuthContext);
  const { SSOSettings } = useContext(SSOSettingsContext);
  const { getKeyItem } = useContext(MessageResponseContext);
  const { t } = useTranslation();

  const confirmModalRef = useRef();

  const [selectedUser, setSelectedUser] = useState(null);
  const [currentAction, setCurrentAction] = useState(USER_ACTIONS.CREATE_USER);
  const userFormModalRef = useRef();

  const { 
    canCreateUser,
    canReadUser,
    canUpdateUser, 
    canDeleteUser,
    // TODO: Please uncomment and enforce RBAC using the below once import and export functionalities are available
    // canImportAllUsers,
    // canExportAllUsers,
    // canImportCompanyAdminUsers,
    // canExportCompanyAdminUsers,
    // canImportManagerUsers,
    // canExportManagerUsers,
    // canImportCompanyUsers,
    // canExportCompanyUsers,
    // canImportDeviceUsers,
    // canExportDeviceUsers,
    // canImportDeviceInternalUsers,
    // canExportDeviceInternalUsers,
  } = usePermissions();

  useEffect(() => {
    if (!canReadUser) {
      return; // TODO: redirect/show unauthorized message
    }
    initManageUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchText) {
      setOrderBy(DEFAULT_ORDER_BY);
      setSearchField(null);
    }else{
      setOrderBy(searchField);
    }
  }, [searchText, searchField]);

  // Helper Functions
  async function initManageUsers() {
    const appSettings = await getAppSettings(context);
    setLoggedInUser(appSettings?.SSOActivationStatus === true 
      ? context.currentUser.uid 
      : context.currentUser.email
    );
    getData(pageSize, orderBy, orderDirection);
  }

  async function getData(currentPageSize, orderBy = DEFAULT_ORDER_BY, orderDirection = DEFAUTL_ORDER_DIRECTION, shouldRefreshCount = false, currentSearchText = searchText, currentSearchField = searchField) {
    setIsLoading(true);
    try {
      setCurrentPage(0);
      setIsSSOMappingEnabled(await getSSOParameterMapping(context));
      
      // Get fresh count if needed
      if (shouldRefreshCount || itemCount === 0) {
        const count = await getTotalCount(context, { searchText: currentSearchText, searchField: currentSearchField, orderDirection });
        setItemCount(count);
      }
  
      const { users, cursors: newCursors } = await fetchUsers({
        context,
        limit: currentPageSize,
        searchField: currentSearchField,
        searchText: currentSearchText,
        orderBy,
        orderDirection,
        totalCount: itemCount
      });
  
      setData(users);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setIsLoading(false);
    }
  }

  async function next() {
    if (!cursors?.last) return;

    setIsLoading(true);
    setCurrentPage(prev => prev + 1);

    try {
      const { users, cursors: newCursors } = await fetchUsers({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        startAfterDoc: cursors.last,
        totalCount: itemCount
      });

      if (users.length > 0) {
        setData(users);
        setCursors(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching next page:', error);
      setIsLoading(false);
    }
  }

  async function prev() {
    if (!cursors?.first) return;

    setIsLoading(true);
    setCurrentPage(prev => prev - 1);

    try {
      const { users, cursors: newCursors } = await fetchUsers({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        endBeforeDoc: cursors.first,
        totalCount: itemCount
      });

      if (users.length > 0) {
        setData(users);
        setCursors(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching previous page:', error);
      setIsLoading(false);
    }
  }

  async function handleFirstPage() {
    setIsLoading(true);
    setCurrentPage(0);
    try {
      const { users, cursors: newCursors } = await fetchUsers({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount
      });

      setData(users);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching first page:', error);
      setIsLoading(false);
    }
  }

  async function handleLastPage() {
    setIsLoading(true);
    try {
      const result = await fetchLastPage({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount
      });
  
      if (result.users.length > 0) {
        setData(result.users);
        setCursors(result.cursors);
  
        const totalPages = Math.ceil(itemCount / pageSize);
        const lastPageNumber = Math.max(0, totalPages - 1);
        setCurrentPage(lastPageNumber);
      } else {
        setData([]);
        setCursors(null);
        setCurrentPage(0);
      }
  
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching last page:', error);
      setData([]);
      setCursors(null);
      setCurrentPage(0);
      setIsLoading(false);
    }
  }

  function handleSearch(text, field) {
    setSearchText(text);
    setSearchField(field);
    handleFetchWithSearch(text, field);
  }

  function handleClearSearch() {
    setSearchText('');
    setSearchField(null);
    getData(pageSize, DEFAULT_ORDER_BY, orderDirection, true, '', null);
  }

  async function handleRefresh() {
    setIsLoading(true);
    try {
      const { users, cursors: newCursors } = await fetchUsers({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        startAtDoc: cursors?.first,
        totalCount: itemCount
      });
  
      setData(users);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error refreshing users:', error);
      setIsLoading(false);
    }
  }

  async function handleFetchWithSearch(text, field) {
    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null);
  
    try {
      // Always get fresh count for new searches
      const count = await getTotalCount(context, { searchText: text, searchField: field, orderDirection });
      setItemCount(count);
  
      const { users, cursors: newCursors } = await fetchUsers({
        context,
        limit: pageSize,
        searchText: text,
        searchField: field,
        orderBy: field,
        orderDirection,
        totalCount: count
      });

      setData(users);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error searching users:', error);
      setIsLoading(false);
      setItemCount(0);
      setData([]);
    }
  }

  async function handleSort(field, direction) {
    if (searchText && field !== searchField) return;

    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null); // Reset cursors on sort change

    try {
      setOrderBy(field);
      setOrderDirection(direction);

      const { users, totalCount, cursors: newCursors } = await fetchUsers({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: field,
        orderDirection: direction
      });

      setData(users);
      setItemCount(totalCount);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setIsLoading(false);
    }
  }

  function onModalClose(modalType, status) {
    setError(false);

    if (modalType === USER_ACTIONS.CREATE_USER) {
      setSnackBarMessage(
        status === 204
          ? t('messages.success.successWithWarning1')
          : t('messages.success.success5')
      );
    } else if (modalType === USER_ACTIONS.EDIT_USER) {
      setSnackBarMessage(t('messages.success.success9'));
    }

    setSnackBarVisible(true);
    getData(pageSize, orderBy, orderDirection, true);
  }

  function handleDeleteUser(user) {
    setIsLoading(true);
    firebaseUtil
      .deleteUser(user, t)
      .then(response => {
        setError(!response.status);
        setSnackBarVisible(true);
        setSnackBarMessage(t(getKeyItem(response.msg)));
        setDeletedUser(null);
        setDeletedUserEmail(null);
        setSearchText('');
        getData(pageSize, orderBy, orderDirection, true, '', null);
        setIsLoading(false);
      })
      .catch(err => {
        setError(!err.status);
        setSnackBarVisible(true);
        setSnackBarMessage(t(getKeyItem(err.msg)));
        setDeletedUser(null);
        setDeletedUserEmail(null);
        getData(pageSize, orderBy, orderDirection);
        setIsLoading(false);
      });
  }

  function onUserCreated() {
    handleRefresh()
  }
  function onUserUpdated() {
    handleRefresh()
  }

  // Table Columns Configuration
  const columns = [
    {
      title: <CustomHeader left>{t('name')}</CustomHeader>,
      field: 'displayName',
      searchable: false,
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.displayName || rowData.name}</CuzCell>
      ),
    },
    {
      title: <CustomHeader left>{t('email')}</CustomHeader>,
      field: 'emailId',
      sorting: true,
      render: rowData => (
        rowData?.emailId?.length > 10 ? (
          <Tooltip title={rowData.emailId} arrow placement="bottom-start">
            <CuzCell>{rowData.emailId}</CuzCell>
          </Tooltip>
        ) : (
          <CuzCell>{rowData.emailId}</CuzCell>
        )
      ),
    },
    {
      title: <CustomHeader>{t('role')}</CustomHeader>,
      field: 'accessType',
      searchable: false,
      sorting: true,
      cellStyle: { textTransform: 'capitalize', textAlign: 'center' },
      render: rowData => (
        <CuzCell>{t(rowData.accessType)}</CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('userType')}</CustomHeader>,
      field: 'authType',
      sorting: true,
      cellStyle: { textAlign: 'center' },
      render: rowData => (
        <CuzCell>{rowData.authType === 1 ? t('sso') : t('nonsso')}</CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('siteName')}</CustomHeader>,
      field: 'siteName',
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.siteName}</CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('createdAt')}</CustomHeader>,
      field: 'reportedAt',
      sorting: true,
      cellStyle: { textAlign: 'center' },
      render: rowData => (
        <CuzCell>
          <NumberToDate
            style={{ fontSize: '14px' }}
            timestamp={rowData.reportedAt}
          />
        </CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('modifiedBy')}</CustomHeader>,
      field: 'modifiedBy',
      sorting: true,
      render: rowData => (
        rowData?.modifiedBy?.length > 10 ? (
          <Tooltip title={rowData.modifiedBy} arrow placement="bottom-start">
            <CuzCell>{rowData.modifiedBy}</CuzCell>
          </Tooltip>
        ) : (
          <CuzCell>{rowData.modifiedBy}</CuzCell>
        )
      ),
    },
    {
      title: <CustomHeader>{t('modifiedAt')}</CustomHeader>,
      field: 'modifiedAt',
      sorting: true,
      cellStyle: { textAlign: 'center' },
      render: rowData => (
        <CuzCell>
          <NumberToDate
            style={{ fontSize: '14px' }}
            timestamp={rowData.modifiedAt}
          />
        </CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('language.label')}</CustomHeader>,
      field: 'language',
      sorting: true,
      cellStyle: { textAlign: 'center' },
      render: rowData => (
        <CuzCell>
          {rowData.language && t(`language.${rowData.language}`)}
        </CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('status')}</CustomHeader>,
      field: 'disabled',
      sorting: true,
      cellStyle: { textAlign: 'center' },
      render: rowData => (
        <CuzCell style={{ color: rowData.disabled ? '#D70015' : '#00864F' }}>
          {rowData.disabled ? t('deactivated') : t('activated')}
        </CuzCell>
      ),
    },
  ];

  const handleCreateUser = () => {
    setCurrentAction(USER_ACTIONS.CREATE_USER);
    setSelectedUser(null);
    userFormModalRef.current.toggleDialog();
  };

  const handleEditUser = (userData) => {
    setCurrentAction(USER_ACTIONS.EDIT_USER);
    setSelectedUser(userData);
    userFormModalRef.current.toggleDialog();
  };

  return (
    <ContentWrapper>
      <CommonPageHeader>
        <CommonPageTitle>{t('manageUsers')}</CommonPageTitle>
        {canCreateUser && (
          <ButtonGroup>
            {/* <CommonButton
              type="outlined"
              onClick={() => {}}
            >
              {t('bulkUpload')}
            </CommonButton>

            <CommonButton
              type="outlined"
              onClick={() => {}}
            >
              {t('exportData')}
            </CommonButton> */}

            <CommonButton
              type="primary"
              onClick={handleCreateUser}
              style={{
                display:
                  SSOSettings.ssoLogin && isSSOMappingEnabled
                    ? 'none'
                    : 'inline-flex',
              }}
            >
              {t('createUser')}
            </CommonButton>
          </ButtonGroup>
        )}
      </CommonPageHeader>

      <Toolbar
        searchText={searchText}
        onSearch={(searchText, searchField) => {
          handleSearch(searchText, searchField);
        }}
        onClear={() => {
          handleClearSearch();
        }}
        multiSearchOpts={[
          { name: t('name'), value: 'displayName' },
          { name: t('email'), value: 'emailId' }
        ]}
        placeholderText={t('searchPlaceholderText')}
      />

      <MainContent>
        <Table
          columns={columns}
          data={data}
          isLoading={isLoading}
          orderBy={orderBy}
          orderDirection={orderDirection}
          isSearchActive={!!searchText}
          searchField={searchField}
          onSort={(field, direction) =>  {
            handleSort(field, direction)
          }}
          pagination={{
            itemCount,
            currentPage,
            rowsPerPage: pageSize,
            rowsPerPageOptions: [10, 20, 50],
            onNextPageClicked: next,
            onPrevPageClicked: prev,
            onFirstPageClicked: handleFirstPage,
            onLastPageClicked: handleLastPage,
            onChangeRowsPerPage: (event) => {
              const newSize = event.target.value;
              setPageSize(newSize);
              setCursors(null);
              getData(newSize, orderBy, orderDirection, false, searchText, searchField);
            },
            hidePagination: itemCount <= DEFAULT_ROWS_PER_PAGE
          }}
          actions={[
            canDeleteUser && (rowData => ({
              hidden:
                rowData.accessType in [ROLES.ZEBRA_SUPER_USER, ROLES.SUPER_ADMIN] ||
                rowData.emailId === loggedInUser,
              icon: () => <DeleteIcon />,
              onClick: (event, rowData) => {
                setDeletedUser(rowData);
                setDeletedUserEmail(rowData.emailId);
                confirmModalRef.current.toggleDialog();
              },
              tooltip: t('deleteUser'),
            })),
            canUpdateUser && (rowData => ({
              hidden: rowData.accessType in [ROLES.ZEBRA_SUPER_USER, ROLES.SUPER_ADMIN] ||
                      rowData.emailId === loggedInUser,
              icon: () => <EditIcon />,
              onClick: (event, rowData) => handleEditUser(rowData),
              tooltip: t('editUser'),
            }))
          ].filter(Boolean)}
          emptyMessage={t('messages.error.err26')}
        />
      </MainContent>

      <BottomNavLinks />

      {/* Conditionally render modals based on permissions */}
      {(canCreateUser || canUpdateUser) && (
        <AddUserModal
          modalRef={userFormModalRef}
          action={currentAction}
          initialData={selectedUser}
          onClose={(status) => {
              onModalClose(currentAction, status);
              setCurrentAction(USER_ACTIONS.CREATE_USER);
          }}
          onUserCreated={onUserCreated}
          onUserUpdated={onUserUpdated}
        />
      )}

      {canDeleteUser && (
        <ConfirmModal
          modalRef={confirmModalRef}
          title={t('deleteUser')}
          message={t('deleteUserConfirmationMsg', { deletedUserEmail })}
          onConfirm={() => {
            confirmModalRef.current.toggleDialog();
            handleDeleteUser(deletedUser);
          }}
        />
      )}

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarVisible}
        autoHideDuration={5000}
        onClose={() => setSnackBarVisible(false)}
      >
        <SnackbarContent error={error}>{snackbarMessage}</SnackbarContent>
      </Snackbar>
    </ContentWrapper>
  );
}

export default withRBAC(ManageUsersPage, RESOURCES.USER_MANAGEMENT);
